/**
 * CF-32664: cart-aware pickup cartWindow.
 *
 * Catalog variations can carry an "Available From / Available Until" range
 * (Thanksgiving preorders that can only be picked up 11/23–11/25). Checkout
 * already rejects a pickup time outside that range, but the date picker used
 * to start at today and make the customer page forward to find a valid day.
 *
 * These pure helpers derive the single cartWindow the whole cart must satisfy so
 * the timepicker can default to it and the date modal can limit its chips.
 */

export interface AvailabilityWindow {
  from: Date;
  until: Date;
}

interface CartLineLike {
  item?: {
    variation?: {
      available_from?: Date | string | null;
      available_until?: Date | string | null;
    };
  };
}

interface IntervalLike {
  start: Date;
}

const toDate = (value: Date | string | null | undefined): Date | null => {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const date = value instanceof Date ? new Date(value.getTime()) : new Date(value);
  return isNaN(date.getTime()) ? null : date;
};

/**
 * Intersection of every restricted item's range: latest `available_from`,
 * earliest `available_until`. Unrestricted items (either date missing) don't
 * narrow the cartWindow. Returns null when nothing in the cart is restricted or
 * the ranges don't overlap, so callers fall back to the regular picker.
 */
export const getCartAvailabilityWindow = (lines: CartLineLike[] | null | undefined): AvailabilityWindow | null => {
  if (!lines || lines.length === 0) {
    return null;
  }

  let from: Date = null;
  let until: Date = null;

  for (const line of lines) {
    const variation = line?.item?.variation;
    const lineFrom = toDate(variation?.available_from);
    const lineUntil = toDate(variation?.available_until);
    if (!lineFrom || !lineUntil) {
      continue;
    }
    if (!from || lineFrom.getTime() > from.getTime()) {
      from = lineFrom;
    }
    if (!until || lineUntil.getTime() < until.getTime()) {
      until = lineUntil;
    }
  }

  if (!from || !until || from.getTime() > until.getTime()) {
    return null;
  }

  return { from, until };
};

/** Inclusive at both ends; a missing cartWindow never restricts. */
export const isWithinAvailabilityWindow = (cartWindow: AvailabilityWindow | null, date: Date): boolean => {
  if (!cartWindow || !date) {
    return true;
  }
  const time = date.getTime();
  return time >= cartWindow.from.getTime() && time <= cartWindow.until.getTime();
};

const startOfDay = (date: Date): Date => new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0);

const endOfDay = (date: Date): Date => new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999);

/**
 * Keeps the calendar days that overlap the cartWindow. Compared by day, so a
 * cartWindow starting at 10:00 still keeps that day's chip; the time slots on it
 * are filtered separately.
 */
export const filterDatesToWindow = (dates: Date[], cartWindow: AvailabilityWindow | null): Date[] => {
  if (!cartWindow) {
    return dates;
  }
  const firstDay = startOfDay(cartWindow.from).getTime();
  const lastDay = endOfDay(cartWindow.until).getTime();
  return dates.filter(date => {
    const time = date.getTime();
    return time >= firstDay && time <= lastDay;
  });
};

export const firstIntervalWithinWindow = <T extends IntervalLike>(intervals: T[], cartWindow: AvailabilityWindow | null): T | null => {
  if (!intervals || intervals.length === 0) {
    return null;
  }
  return intervals.find(interval => isWithinAvailabilityWindow(cartWindow, interval.start)) || null;
};

export const availabilityWindowText = (cartWindow: AvailabilityWindow | null, formatDate: (date: Date) => string): string => {
  if (!cartWindow) {
    return '';
  }
  const from = formatDate(cartWindow.from);
  const until = formatDate(cartWindow.until);
  if (from === until) {
    return `Items in your cart can only be picked up on ${from}.`;
  }
  return `Items in your cart can only be picked up between ${from} and ${until}.`;
};
